// „Продажби по периоди" — `GET /reports/analytics/sales-by-periods`.
//
// Един ред = един отрязък от времето. `mode` решава дали дните се сливат
// („grouped": 13:00 – 13:59 за целия период) или вървят хронологично
// („timeline"). Decimal полетата идват като JSON НИЗ — парсват се явно.

import { api } from "./client";

export type SalesByPeriodsMode = "grouped" | "timeline";
export type SalesByPeriodsUnit = "hour" | "weekday" | "month" | "date" | "year";

/** Кои единици има всеки режим — огледало на `UNITS_BY_MODE` в BE. */
export const SALES_BY_PERIODS_UNITS: Record<
  SalesByPeriodsMode,
  SalesByPeriodsUnit[]
> = {
  grouped: ["hour", "weekday", "month"],
  timeline: ["hour", "date", "month", "year"],
};

/** Сума/брой сметки по метод; смесеното е разпределено в „В брой" и „С карта". */
export interface SalesByPeriodsMethodAmount {
  payment_method_id: number;
  amount: string;
  count: number;
}

/** Оборотът на една станция в отрязъка (продуктът — при основната си станция). */
export interface SalesByPeriodsStationAmount {
  station_id: number;
  amount: string;
}

export interface SalesByPeriodsDay {
  date: string;
  bills: number;
  revenue: string;
}

export interface SalesByPeriodsTotals {
  bills: number;
  /** Сметките с ненулев оборот — знаменателят на „Средна сметка" (0.00 не се брои). */
  paid_bills: number;
  /** Различни клиенти (`Bill.customer_id`); сметките без клиент не се броят. */
  clients: number;
  /** Частта от `revenue` по сметки с клиент. */
  client_revenue: string;
  quantity: string;
  /** Реално платеното след отстъпка/надбавка. */
  revenue: string;
  /** Частта от `revenue` по сметки с номер на бон. */
  fiscalized_revenue: string;
  /** `price × quantity` по менюто — преди отстъпка/надбавка. */
  base_amount: string;
  /** Отстъпки и надбавки поотделно, по знака на всеки ред; и двете ≥ 0. */
  discount: string;
  markup: string;
  vat_amount: string;
  net_amount: string;
  /** Кеш/карта с пропорционално разпределено смесено плащане. */
  cash: string;
  card: string;
  methods: SalesByPeriodsMethodAmount[];
  /** Сборът по станции е `revenue`. */
  stations: SalesByPeriodsStationAmount[];
}

export interface SalesByPeriodsRow extends SalesByPeriodsTotals {
  /** Сортируем ключ („13", „2026-06-29", „2026-06"). */
  key: string;
  hour: number | null;
  /** Понеделник = 0. */
  weekday: number | null;
  month: number | null;
  year: number | null;
  date: string | null;
  days: SalesByPeriodsDay[];
}

export interface SalesByPeriodsResponse {
  mode: SalesByPeriodsMode;
  unit: SalesByPeriodsUnit;
  rows: SalesByPeriodsRow[];
  summary: SalesByPeriodsTotals;
}

export interface SalesByPeriodsFilters {
  start_date?: string;
  end_date?: string;
  mode: SalesByPeriodsMode;
  unit: SalesByPeriodsUnit;
  station_ids?: number[];
  item_ids?: number[];
  user_ids?: number[];
  client_ids?: number[];
  /** Включва и сметките без клиент (отделна опция, не клиент с id). */
  without_client?: boolean;
}

export const getSalesByPeriodsReport = async (
  filters: SalesByPeriodsFilters,
): Promise<SalesByPeriodsResponse> => {
  const params = new URLSearchParams();
  if (filters.start_date) params.append("start_date", filters.start_date);
  if (filters.end_date) params.append("end_date", filters.end_date);
  params.append("mode", filters.mode);
  params.append("unit", filters.unit);
  for (const id of filters.station_ids ?? []) params.append("station_ids", String(id));
  for (const id of filters.item_ids ?? []) params.append("item_ids", String(id));
  for (const id of filters.user_ids ?? []) params.append("user_ids", String(id));
  for (const id of filters.client_ids ?? []) params.append("client_ids", String(id));
  if (filters.without_client) params.append("without_client", "true");

  const response = await api.get<SalesByPeriodsResponse>(
    `/reports/analytics/sales-by-periods?${params.toString()}`,
  );
  return response.data;
};
